import React from 'react';
import isEqual from 'react-fast-compare';
import {useSelector} from 'react-redux';
import {UserService} from '../api';
import {DashboardStationConfig} from '../store/weather';
import {selectDashboardConfig, selectIsLoggedIn} from '../store';
import useApiClient from './useApiClient';

// Collapse rapid drag-reorder / show-hide edits into a single PATCH.
const DASHBOARD_SYNC_DEBOUNCE_MS = 800;

type DashboardConfigMap = Record<string, DashboardStationConfig>;

/**
 * Pushes per-station dashboard layout changes (weather.dashboardConfig) up to
 * the backend so iPhone/iPad/webapp converge. This hook covers
 * `dashboard_config` and nothing else -- the forecast model preference is
 * `useForecastPreferenceSync`'s, and the two stay apart because `useApiClient`
 * debounces per URL per hook instance, so one `fetch` shared between them
 * would let either PATCH supersede the other's. The policy here:
 *  - anonymous users never hit the server (config stays purely local)
 *  - only the station key(s) that changed are PATCHed; the backend merges
 *    per-station-key. A key removed locally is PATCHed as a null tombstone so
 *    the backend (whose merge is upsert-only) drops it instead of lingering.
 *  - the freshly-hydrated server state is never echoed back: the first run is
 *    skipped, and the anonymous->logged-in transition re-seeds the baseline
 *    instead of diffing the pre-login local config against the server map.
 *  - rapid edits are debounced; transient errors are ignored (local persist is
 *    the source of truth and the next edit retries)
 *
 * The "last synced" baseline only advances once a PATCH actually goes out, so
 * the diff stays CUMULATIVE across a debounce window: editing station A then
 * station B within 800ms sends {A, B} in one PATCH. Advancing the baseline on
 * every render would make each diff incremental, and the shared debounce timer
 * in useApiClient would then drop all but the last call — silently losing A's
 * edit server-side.
 *
 * Wire this once near the top of the tree (the UserSyncProviders).
 */
const useDashboardConfigSync = () => {
  const {fetch} = useApiClient();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const dashboardConfig = useSelector(
    selectDashboardConfig,
    isEqual,
  ) as DashboardConfigMap;

  // Config as of the last value we pushed to (or hydrated from) the server.
  // Advances only when a PATCH actually resolves (see the .then below) so the
  // diff stays cumulative across debounced edits. Seeded on mount so the
  // initial hydrated render does not echo server state straight back.
  const lastSyncedRef = React.useRef<DashboardConfigMap | null>(null);
  // Tracks the login edge so the anonymous->logged-in hydration is not echoed.
  const wasLoggedInRef = React.useRef(isLoggedIn);

  React.useEffect(() => {
    const justLoggedIn = isLoggedIn && !wasLoggedInRef.current;
    wasLoggedInRef.current = isLoggedIn;

    const baseline = lastSyncedRef.current;

    // Skip the first run (hydration) and the login transition: in both cases
    // the current config IS the freshly-hydrated server state (or the local
    // config useUserData just pushed up itself), so re-seed and push nothing.
    if (baseline === null || justLoggedIn) {
      lastSyncedRef.current = dashboardConfig;
      return;
    }

    // Anonymous edits stay purely local; keep the baseline current so a later
    // login does not replay them as server writes.
    if (!isLoggedIn) {
      lastSyncedRef.current = dashboardConfig;
      return;
    }

    if (isEqual(baseline, dashboardConfig)) {
      return;
    }

    // Cumulative diff against the last synced baseline: station keys that
    // changed (added or value differs deeply), plus keys removed locally —
    // sent as null tombstones so the backend's upsert-only merge drops them
    // rather than keeping stale config forever.
    const changed: Record<string, DashboardStationConfig | null> = {};
    Object.keys(dashboardConfig).forEach((stationKey) => {
      if (!isEqual(baseline[stationKey], dashboardConfig[stationKey])) {
        changed[stationKey] = dashboardConfig[stationKey];
      }
    });
    Object.keys(baseline).forEach((stationKey) => {
      if (!(stationKey in dashboardConfig)) {
        changed[stationKey] = null;
      }
    });

    if (Object.keys(changed).length === 0) {
      return;
    }

    // Fire-and-forget: send ONLY the changed station key(s). Advance the
    // baseline only once the (debounced) PATCH resolves — calls the debounce
    // supersedes reject with DebounceSupersededError (absorbed below), so
    // the surviving call always carries the full cumulative diff. On failure
    // the local persist stays the source of truth and the next edit retries.
    const snapshot = dashboardConfig;
    fetch(UserService.updateUserProfile({dashboard_config: changed}), {
      debounce: DASHBOARD_SYNC_DEBOUNCE_MS,
    })
      .then(() => {
        lastSyncedRef.current = snapshot;
      })
      .catch(() => {});
  }, [dashboardConfig, isLoggedIn, fetch]);
};

export default useDashboardConfigSync;
